import type { V1AssetCardProps } from "@v1/components/assets/AssetCard";
import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { V1AssetCard } from "@v1/components/assets/AssetCard";
import Label from "@v1/components/designSystem/label/Label";
import { FileUploadOverlay } from "@v2/features/assets/components/FileUploadOverlay";
import { formatAssetDisplayName } from "@v2/features/assets/utils/formatAssetDisplayName";
import { format } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatFileSize } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

type AssetsGridProps = {
  assets: Asset[];
  searchTerm: string;
  droppedAssets?: Asset[];
  onAssetSelected?: (asset: Asset) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  isDragActive?: boolean;
  emptyState: React.ReactNode;
  shouldAllowActions?: boolean;
  className?: string;
  assetTypes?: ("image" | "video")[];
};

const AssetGridContext = React.createContext<{
  internalAssets: Asset[];
  droppedAssets: Asset[];
  onAssetSelected: ((asset: Asset) => void) | undefined;
  shouldAllowActions: boolean;
} | null>(null);

const useAssetGridContext = () => useRequiredContext(AssetGridContext);

const AssetsGridRoot: React.FC<React.PropsWithChildren<AssetsGridProps>> = ({
  children,
  searchTerm,
  hasNextPage,
  fetchNextPage,
  isLoading,
  isDragActive = false,
  assets,
  droppedAssets = [],
  emptyState,
  onAssetSelected,
  shouldAllowActions = false,
  className,
  assetTypes = ["image", "video"],
}) => {
  const gridRef = React.useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies(searchTerm): searchTerm changing is used to reset the scroll position
  React.useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  // NOTE (Gabe 2025-03-06): internalAssets is used to prevent flashes when
  // search results are loading.
  const [internalAssets, setInternalAssets] = React.useState<Asset[]>(assets);
  React.useEffect(() => {
    if (assets.length > 0 || !isLoading) {
      setInternalAssets(assets);
    }
  }, [assets, isLoading]);

  if (internalAssets.length === 0 && droppedAssets.length === 0 && isLoading) {
    return <AssetsGridSkeleton length={7} />;
  }

  if (internalAssets.length === 0 && droppedAssets.length === 0) {
    return emptyState;
  }

  const contextValue = {
    internalAssets,
    droppedAssets,
    onAssetSelected,
    shouldAllowActions,
  };

  return (
    <AssetGridContext.Provider value={contextValue}>
      <div
        id="assets-grid"
        className={twMerge(
          "flex flex-1 min-h-0 cursor-default flex-col overflow-auto relative bg-sheet border-0.5 border-border rounded-sm border-dashed",
          className,
        )}
        ref={gridRef}
      >
        <FileUploadOverlay
          isDragging={isDragActive}
          acceptedTypes={assetTypes}
        />
        {!isDragActive && (
          <InfiniteScroll
            dataLength={assets.length}
            next={() => void fetchNextPage()}
            hasMore={hasNextPage}
            scrollableTarget="assets-grid"
            loader={<AssetsGridSkeleton length={3} />}
          >
            <div className="grid w-full gap-2 p-2 pb-4 grid-cols-1 md:grid-cols-2">
              {children}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </AssetGridContext.Provider>
  );
};

const AssetCardDetailed: React.FC<V1AssetCardProps> = (props) => {
  const { asset } = props;
  const displayName = formatAssetDisplayName(asset.name);

  return (
    <V1AssetCard.Root {...props}>
      <V1AssetCard.Anchor
        shouldAllowDrag={props.shouldAllowDrag}
        onClick={props.onClick}
      >
        <V1AssetCard.AnchorFooter
          shouldAllowActions={props.shouldAllowActions}
        />
      </V1AssetCard.Anchor>
      <V1AssetCard.Content>
        <span className="w-full break-all text-xs font-medium">
          {displayName}
        </span>
        <div className="text-muted flex gap-1 whitespace-nowrap text-xs">
          <div className="bg-selectable-selected rounded-sm">
            <Label
              label={
                <span className="text-primary px-1 py-0.5 uppercase">
                  {asset.fileExtension}
                </span>
              }
              size="sm"
              layoutClassName="w-fit h-4"
            />
          </div>
          {asset.width && asset.height && (
            <span>
              {asset.width}x{asset.height}
            </span>
          )}
          {asset.sizeBytes && (
            <>
              <span>•</span>
              <span>{formatFileSize(asset.sizeBytes)}</span>
            </>
          )}
        </div>
        {asset.createdAt && (
          <span className="text-muted text-xs">
            Added {format(asset.createdAt, "MMM d, yyyy")}
          </span>
        )}
      </V1AssetCard.Content>
    </V1AssetCard.Root>
  );
};

const AssetsGridListDetailed = () => {
  const { internalAssets, droppedAssets, onAssetSelected, shouldAllowActions } =
    useAssetGridContext();

  return (
    <>
      {droppedAssets?.map((asset: Asset) => (
        <AssetCardDetailed
          key={asset.id}
          isUploading
          asset={asset}
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
        />
      ))}

      {internalAssets.map((asset: Asset) => (
        <AssetCardDetailed
          key={asset.id}
          asset={asset}
          onClick={
            onAssetSelected
              ? () => {
                  onAssetSelected(asset);
                }
              : undefined
          }
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
          className="h-full"
        />
      ))}
    </>
  );
};

const AssetsGridListSimplified = () => {
  const { internalAssets, droppedAssets, onAssetSelected, shouldAllowActions } =
    useAssetGridContext();

  return (
    <>
      {droppedAssets?.map((asset: Asset) => (
        <V1AssetCard
          key={asset.id}
          isUploading
          asset={asset}
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
        />
      ))}

      {internalAssets.map((asset: Asset) => (
        <V1AssetCard
          key={asset.id}
          asset={asset}
          onClick={
            onAssetSelected
              ? () => {
                  onAssetSelected(asset);
                }
              : undefined
          }
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
          className="h-full"
        />
      ))}
    </>
  );
};

export const V1AssetsGrid = (props: AssetsGridProps) => {
  return (
    <AssetsGridRoot {...props}>
      <AssetsGridListSimplified />
    </AssetsGridRoot>
  );
};

V1AssetsGrid.Root = AssetsGridRoot;
V1AssetsGrid.ListDetailed = AssetsGridListDetailed;
V1AssetsGrid.ListSimplified = AssetsGridListSimplified;

const AssetsGridSkeleton: React.FC<{
  length: number;
}> = ({ length }) => {
  return (
    <div className="grid w-full gap-2 p-2 grid-cols-1 md:grid-cols-2">
      {Array.from({ length }).map((_, index) => (
        <div key={index} className="flex flex-col gap-1">
          <Skeleton className="aspect-square w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-12" />
        </div>
      ))}
    </div>
  );
};
